import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';

import Grid from '@material-ui/core/Grid';
import EditIcon from '@material-ui/icons/EditOutlined';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Stars from '@material-ui/icons/Stars';
import CheckCircle from '@material-ui/icons/CheckCircle';
import AddIcon from '@material-ui/icons/Add';

import Tooltip from 'components/Tooltip/Tooltip';
import { ViewMode } from './viewModes';
import BankAccountModal from './BankAccountModal';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import { BankAccountsStore } from 'stores/workflowStores/finances/bankAccounts/bankAccountsStore';
import { BankData, BankAccountOwnership } from 'stores/workflowStores/finances/bankAccounts/model';
import { RootStore } from 'stores/rootStore';
import { PersonType, EdwardColors, Currency } from 'enums';
import useStyles from './styles/moduleStyles';
import classNames from 'classnames';

interface Props {
  accountId: string;
  rootStore: RootStore;
  //openEdit?: () => void;
  readOnly?: boolean;
  viewMode?: ViewMode;
  legal?: boolean;
  errors?: string[];
}

const BankAccounts: React.FC<Props> = ({ accountId, rootStore, readOnly, viewMode, legal, errors }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [isBankAccountOpen, setIsBankAccountOpen] = useState(false);

  const [bankAccountId, setBankAccountId] = useState<string | undefined>(undefined);
  const [bankAccountsStore] = useState(new BankAccountsStore(rootStore, accountId, viewMode));
  const [displayError, setDisplayError] = useState<string[] | undefined>(errors);

  useEffect(() => {
    bankAccountsStore.fetchBankData();
  }, [bankAccountsStore]);

  useEffect(() => {
    setDisplayError(errors);
  }, [errors]);

  const handleBankAccountClose = () => {
    setIsBankAccountOpen(false);
  };

  const editBankAccount = (bankAccountId: string) => {
    setBankAccountId(bankAccountId);
    setIsBankAccountOpen(true);
  };

  const handleRefresh = () => {
    bankAccountsStore.fetchBankData();
    setDisplayError(undefined);
  };

  const [confirmDialog, setConfirmDialog] = useState<string | undefined>(undefined);
  const [confirmType, setConfirmType] = useState<'DELETE' | 'PRIMARY' | null>(null);


  const iconColorByCurrencyMap: { [key in Currency]?: EdwardColors } = {
    [Currency.USD]: EdwardColors.GRAY_MEDIUM,
    [Currency.CZK]: EdwardColors.SECONDARY,
    [Currency.EUR]: EdwardColors.BLUE
  };

  const listBankAccounts = (data: BankData) => {
    return (
      <>
        {data.bankAccounts.map((account) => {
          return (
            <Grid container item xs={12} spacing={1} key={account.id}>
              <Grid
                container
                item
                alignContent="center"
                alignItems="center"
                className={classNames(classes.item, classes.bankAccount, {
                  [classes.primary]: account.primary && data.currency === account.currency
                })}
              >
                <Grid item xs={1} style={{ textAlign: 'center' }}>
                  {account.primary ? (
                    <Tooltip
                      title={
                        <>
                          {t('bankAccount_primary_info1')}
                          <br />
                          <span
                            dangerouslySetInnerHTML={{
                              __html: t('bankAccount_primary_info2', {
                                interpolation: { escapeValue: false },
                                currency: account.currency,
                              }),
                            }}
                          />
                          {viewMode !== ViewMode.DETAIL && t('bankAccount_primary_info3')}
                        </>
                      }
                    >
                      <span style={{ color: iconColorByCurrencyMap[account.currency] }}>
                        <Stars fontSize="medium" />
                      </span>
                    </Tooltip>
                  ) : viewMode === ViewMode.DETAIL ? (
                    <Tooltip
                      title={
                        !account.verified || readOnly || data.currency !== account.currency
                          ? ''
                          : t(`bankAccount_setPrimary_${account.currency}`)
                      }
                    >
                      <IconButton
                        size="medium"
                        className="account-primary-button"
                        onClick={() => {
                          setConfirmType('PRIMARY');
                          setConfirmDialog(account.id);
                        }}
                        disabled={!account.verified || readOnly || data.currency !== account.currency}
                      >
                        <Stars fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    ''
                  )}
                  {account.verified && (
                    <Tooltip title={`${t('bankAccount_verified')} ${account.currency}`}>
                      <span style={{ color: iconColorByCurrencyMap[account.currency] }}>
                        <CheckCircle />
                      </span>
                    </Tooltip>
                  )}
                </Grid>
                <Grid item xs={9}>
                  {t('general_bankAccount_fullNumber')}:
                  <strong> {account.hasIban ? account.iban : account.fullNumber}</strong>{' '}
                  {!account.hasIban && <> ({account.bank})</>}, {t('general_currency')}:
                  <strong> {account.currency}</strong>
                  <span className={classes.divider} />
                  {(account.ownerFirstname && account.ownerFirstname !== '') ||
                  (account.ownerCompanyName && account.ownerCompanyName !== '') ? (
                    <>
                      {t('general_bankAccount_fullName')}:{' '}
                      {account.ownerCompanyName ? (
                        <strong>{account.ownerCompanyName}</strong>
                      ) : (
                        <strong>{`${account.ownerFirstname} ${account.ownerLastname}`}</strong>
                      )}
                    </>
                  ) : (
                    <strong className={classes.error}>
                      {t('globalErrors_account.clientData.bankData.ownership.missing')}
                    </strong>
                  )}
                  {account.ownership === BankAccountOwnership.JOINED && ` (${t('general_bankAccount_SJM')})`}
                </Grid>
                <Grid item xs={2} className={classes.actions}>
                  <IconButton
                    size="medium"
                    className="accountEdit-button"
                    onClick={() => editBankAccount(account.id!)}
                    disabled={readOnly}
                  >
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                  <Tooltip
                    title={
                      viewMode === ViewMode.DETAIL && account.knownToOcp && account.primary
                        ? t('tooltip_accountDetail_deleteBankAccount')
                        : ''
                    }
                    placement="top"
                  >
                    <span>
                      <IconButton
                        size="medium"
                        className="account-delete-button"
                        onClick={() => {
                          setConfirmType('DELETE');
                          setConfirmDialog(account.id);
                        }}
                        disabled={readOnly || (account.knownToOcp && account.primary)}
                      >
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
          );
        })}
        {data.addingEnabled && (
          <Grid container item xs={12} spacing={1}>
            <Grid
              container
              item
              className={classNames(classes.item, classes.addBankAccount)}
              alignContent="center"
              alignItems="center"
            >
              <Button
                variant="contained"
                color="primary"
                id="additional-target-button"
                onClick={async () => {
                  //openEdit !== undefined && readOnly && (await openEdit());
                  setBankAccountId(undefined);
                  setIsBankAccountOpen(true);
                }}
                startIcon={<AddIcon fontSize="medium" />}
                disabled={readOnly}
              >
                {t('bankAccount_addAccount')}
              </Button>
            </Grid>
          </Grid>
        )}
      </>
    );
  };

  return bankAccountsStore.bankData ? (
    <>
      <BankAccountModal
        opened={isBankAccountOpen}
        handleClose={handleBankAccountClose}
        bankAccountId={bankAccountId}
        bankAccountsStore={bankAccountsStore}
        refresh={handleRefresh}
        options={bankAccountsStore.bankData.options}
        legal={bankAccountsStore.bankData.personType === PersonType.LEGAL || legal}
        accounts={bankAccountsStore.bankData.bankAccounts.length}
        currency={bankAccountsStore.bankData.currency}
      />

      <ConfirmDialog
        noHeader
        isOpen={confirmDialog !== undefined}
        confirm={async () => {
          if (confirmType === 'PRIMARY') {
            await bankAccountsStore?.setBankAccountPrimary(confirmDialog!);
          } else {
            await bankAccountsStore?.deleteBankAccount(confirmDialog!);
          }
          setConfirmType(null);
          setConfirmDialog(undefined);
          handleRefresh();
        }}
        close={() => {
          setConfirmDialog(undefined);
        }}
      >
        {t(`bankAccount_confirm_${confirmType}`)}
      </ConfirmDialog>

      <Typography variant="h3">{t('general_bankAccount')}</Typography>
      {displayError && <p style={{ color: EdwardColors.ALERT }}>{displayError.map((err) => t(err))}</p>}
      {listBankAccounts(bankAccountsStore.bankData)}
    </>
  ) : null;
};

export default compose(inject('rootStore'), observer)(BankAccounts);
